import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HandleRequestData } from '../../interfaces/handleRequest';
import businessDataApi from '../../api/businessDataApi';
import { AppDispatch, GlobalState } from '../../../main/store';
import { ExtendedBusinessDataStoreState } from '../../interfaces/businessDataStoreState';
import parseBoolean from '../../utils/parseBoolean';
import { CourseDomainItemData } from '../../interfaces/businessDataItem/courseDomainItem';
import { CourseDomainResponseData } from '../../interfaces/businessDataResponse/courseDomainResponse';
import {
    ExtendedBusinessDataStoreInitialState,
    getActiveBusinessDataValues,
    resetBasicBusinessDataStoreState,
} from '../store.common';
import { BusinessDataApiQueryParams } from '../../interfaces/queryParams';

/**
 * courseDomainsSlice manages all courseDomains state, and contains courseDomains actions as well as courseDomains state reducers.
 * Note that while the logic in the reducers appears to mutate the state, it does not.
 * The redux toolkit uses Immer to ensure that no mutations occur.
 */
export const courseDomainsSlice = createSlice({
    name: 'courseDomains',
    initialState: {
        ...ExtendedBusinessDataStoreInitialState,
    } as ExtendedBusinessDataStoreState<CourseDomainItemData>,
    reducers: {
        setCourseDomainsList: (
            state,
            action: PayloadAction<CourseDomainItemData[]>,
        ) => {
            const byCourseDomainName = action.payload.reduce(
                (
                    byCourseDomainName: {
                        [key: string]: CourseDomainItemData;
                    },
                    courseDomain: CourseDomainItemData,
                ) => {
                    byCourseDomainName[courseDomain.course_domain] = {
                        ...courseDomain,
                        active: parseBoolean(courseDomain.active),
                    };
                    return byCourseDomainName;
                },
                {},
            );
            state.entities = byCourseDomainName;
            state.keysList = Object.keys(byCourseDomainName);
        },
        setCourseDomain: (
            state,
            action: PayloadAction<CourseDomainItemData>,
        ) => {
            // this reducer may be used when adding a new program or updating an existing one.
            // only add to keysList and update count if adding a new program
            if (!state.entities[action.payload.course_domain]) {
                state.keysList = [
                    ...state.keysList,
                    action.payload.course_domain,
                ];
                state.count = state.keysList.length;
            }
            state.entities[action.payload.course_domain] = action.payload;
        },
        setSelectedCourseDomain: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.selectedItemKey = action.payload;
        },
        setError: (state, action: PayloadAction<any>) => {
            state.error = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsLoaded: (state, action: PayloadAction<boolean>) => {
            state.isLoaded = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | null>) => {
            state.searchText = action.payload;
        },
        setActive: (
            state,
            action: PayloadAction<BusinessDataApiQueryParams.Active>,
        ) => {
            state.active = action.payload;
        },
        setFrom: (state, action: PayloadAction<number>) => {
            state.from = action.payload;
        },
        setPagesCount: (state, action: PayloadAction<number>) => {
            state.pagesCount = action.payload;
        },
        setCurrentPageIndex: (state, action: PayloadAction<number>) => {
            state.currentPageIndex = action.payload;
        },
        setCount: (state, action: PayloadAction<number>) => {
            state.count = action.payload;
        },
        resetCourseDomainsSlice: resetBasicBusinessDataStoreState,
    },
});

export const {
    setIsLoading,
    setIsLoaded,
    setCourseDomainsList,
    setCourseDomain,
    setSelectedCourseDomain,
    setError,
    setCount,
    setSearchText,
    setActive,
    setFrom,
    setPagesCount,
    setCurrentPageIndex,
    resetCourseDomainsSlice,
} = courseDomainsSlice.actions;

const handleCourseDomainsListRequest = () => {
    return async (dispatch: AppDispatch) => {
        try {
            const {
                result: { COURSE_DOMAIN, total_count },
            }: HandleRequestData<CourseDomainResponseData> =
                await businessDataApi.getCourseDomains({ active: 'all' });
            dispatch(setCourseDomainsList(COURSE_DOMAIN));
            dispatch(setCount(total_count));
        } catch (error: any) {
            dispatch(setError(error?.message || 'getCourseDomains API error'));
        }
    };
};

export const getCourseDomainList = () => {
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        dispatch(setIsLoading(true));
        await dispatch(handleCourseDomainsListRequest());
        dispatch(setIsLoading(false));
        if (!state.courseDomains.isLoaded) {
            dispatch(setIsLoaded(true));
        }
    };
};

/**
 * action to update program
 * before update api call, fetch program types and prepare payload containing only the changed fields
 * after update api call, fetch program types and update the current item in that keysList with the item data we updated
 * @param data
 */
export const updateCourseDomain = (data: CourseDomainItemData) => {
    return async (
        dispatch: AppDispatch,
        getState: () => GlobalState,
    ): Promise<boolean> => {
        const state = getState();
        // immediately put the keysList into loading while we finish updating the item/fetching the keysList
        dispatch(setIsLoading(true));
        if (!data.pk || data?.pk.length <= 0) {
            // eslint-disable-next-line no-console
            console.error(
                `Error updating new program: required field pk was missing`,
            );
            dispatch(setIsLoading(false));
            return false;
        }
        try {
            await businessDataApi.updateCourseDomain(data.pk, data.active);
            await dispatch(handleCourseDomainsListRequest());
            // force-update the item in the keysList for now, since the keysList we fetched may not have this item's update yet
            dispatch(
                setCourseDomain({
                    ...state.courseDomains.entities[data.course_domain],
                    active: data.active,
                }),
            );
            dispatch(setIsLoading(false));
            return true;
        } catch (error: any) {
            dispatch(setIsLoading(false));
            return false;
        }
    };
};

export const addCourseDomain = (active = true, courseDomainName: string) => {
    return async (
        dispatch: AppDispatch,
        getState: () => GlobalState,
    ): Promise<boolean> => {
        const state = getState();
        dispatch(setIsLoading(true));
        try {
            await businessDataApi.addCourseDomain(courseDomainName, active);
            // now refresh the keysList
            await dispatch(handleCourseDomainsListRequest());
            if (!state.courseDomains?.entities[courseDomainName]) {
                // shove the item into the keysList, but only if it's not there already
                const newCourseDomainData: CourseDomainItemData = {
                    course_domain: courseDomainName,
                    active: active,
                };
                dispatch(setCourseDomain(newCourseDomainData));
            }
            dispatch(setIsLoading(false));
            return true;
        } catch (error: any) {
            dispatch(setIsLoading(false));
            return false;
        }
    };
};

export const selectAllCourseDomains = (state: GlobalState) => {
    return state.courseDomains.keysList.map(
        (courseDomainName) => state.courseDomains.entities[courseDomainName],
    );
};

export const selectAllActiveCourseDomains = (
    state: GlobalState,
): Array<CourseDomainItemData> => {
    return getActiveBusinessDataValues(
        state.courseDomains.keysList,
        state.courseDomains.entities,
    );
};

export const selectIsLoading = (state: GlobalState) =>
    state.courseDomains.isLoading;

export const selectIsLoaded = (state: GlobalState) =>
    state.courseDomains.isLoaded;

export const selectSelectedCourseDomain = (state: GlobalState) => {
    return state?.courseDomains?.selectedItemKey
        ? state?.courseDomains?.entities[state?.courseDomains?.selectedItemKey]
        : null;
};

export const selectError = (state: GlobalState) => state.courseDomains.error;

export const selectCount = (state: GlobalState) => state.courseDomains.count;

export const selectSearchText = (state: GlobalState) =>
    state.courseDomains.searchText;

export const selectPagesCount = (state: GlobalState) =>
    state.courseDomains.pagesCount;

export const selectCurrentPageIndex = (state: GlobalState) =>
    state.courseDomains.currentPageIndex;

export default courseDomainsSlice.reducer;
